import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function LeadForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const { ref, isVisible } = useScrollAnimation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea') as HTMLTextAreaElement | null;
    if (textarea && textarea.value.length > 500) return;
    setStatus('sending');
    try {
      const formData = new FormData(form);
      const body = new URLSearchParams(
        Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
      );
      const res = await fetch('https://readdy.ai/api/form/d6tdsucj8d20juc902b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-28 bg-brand-dark relative overflow-hidden" id="book">
      {/* Background decorative element */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px]" style={{ background: 'radial-gradient(circle, #D97706 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px]" style={{ background: 'radial-gradient(circle, #D97706 0%, transparent 65%)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-14 items-start">
          {/* Left: Info */}
          <div
            ref={ref}
            className={`lg:w-5/12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-6">
              <div className="h-px w-8 bg-brand-amber" />
              Book Your Service
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
              Get a Free Quote from Jacksonville's Cleaning Experts
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-10">
              Fill out the form and our team will contact you within the hour with a free, no-obligation quote. Same-week appointments throughout Jacksonville and surrounding areas.
            </p>

            {/* Benefit list */}
            <div className="space-y-4 mb-10">
              {[
                { icon: 'ri-check-double-fill', text: 'Free quotes with zero obligation' },
                { icon: 'ri-timer-flash-fill', text: 'Response within 1 hour during business hours' },
                { icon: 'ri-calendar-check-fill', text: 'Same-week scheduling available' },
                { icon: 'ri-price-tag-3-fill', text: 'Upfront, transparent pricing — no surprises' },
                { icon: 'ri-camera-3-fill', text: 'Before & after photos on every job' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 text-white/70">
                  <div className="w-8 h-8 flex items-center justify-center bg-brand-amber/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-brand-amber text-sm`} />
                  </div>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Phone CTA */}
            <div className="p-6 bg-white/8 border border-white/12 rounded-2xl">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-brand-amber rounded-2xl flex-shrink-0">
                  <i className="ri-phone-fill text-white text-xl" />
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-1">Prefer to call?</div>
                  <a
                    href="tel:+19045550191"
                    className="font-serif font-bold text-white text-2xl hover:text-brand-amber transition-colors cursor-pointer"
                  >
                    (904) 555-0191
                  </a>
                  <div className="text-white/40 text-xs mt-0.5">Mon–Fri 8am–6pm · Sat 9am–3pm</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form card */}
          <div className="lg:w-7/12 w-full">
            <div className="bg-white rounded-3xl p-8 lg:p-10">
              {status === 'success' ? (
                <div className="text-center py-14">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-5 border-2 border-green-200">
                    <i className="ri-check-double-fill text-green-500 text-4xl" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-brand-dark mb-3">Request Received!</h3>
                  <p className="text-brand-warm-gray max-w-sm mx-auto leading-relaxed">
                    We'll contact you within the hour with your free quote. Thank you for choosing BBM Services — Jacksonville's home safety experts.
                  </p>
                </div>
              ) : (
                <form id="bbm-lead-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="mb-7">
                    <h3 className="font-serif text-2xl font-bold text-brand-dark mb-1">Request Your Free Quote</h3>
                    <p className="text-brand-warm-gray text-sm">We respond within 1 hour, guaranteed.</p>
                  </div>

                  {/* Name row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors"
                        placeholder="Smith"
                      />
                    </div>
                  </div>

                  {/* Contact row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors"
                        placeholder="(904) 555-0000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors"
                        placeholder="john@email.com"
                      />
                    </div>
                  </div>

                  {/* Service */}
                  <div className="mb-4">
                    <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">Service Needed *</label>
                    <select
                      name="service"
                      required
                      className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors"
                    >
                      <option value="">Select a service...</option>
                      <option value="Chimney Cleaning">Chimney Cleaning</option>
                      <option value="Air Duct Cleaning">Air Duct Cleaning</option>
                      <option value="Dryer Vent Cleaning">Dryer Vent Cleaning</option>
                      <option value="Multiple Services">Multiple Services</option>
                    </select>
                  </div>

                  {/* Address */}
                  <div className="mb-4">
                    <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">Address / Zip Code</label>
                    <input
                      type="text"
                      name="address"
                      className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors"
                      placeholder="Jacksonville, FL 32099"
                    />
                  </div>

                  {/* Message */}
                  <div className="mb-7">
                    <label className="block text-xs font-bold text-brand-dark uppercase tracking-wide mb-1.5">Additional Details</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-amber transition-colors resize-none"
                      placeholder="Anything specific we should know? (max 500 characters)"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-4 rounded-xl text-base transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {status === 'sending' ? 'Sending Request...' : 'Get My Free Quote Now'}
                  </button>
                  {status === 'error' && (
                    <p className="text-red-500 text-sm text-center mt-3">
                      Something went wrong. Please call us at (904) 555-0191.
                    </p>
                  )}
                  <p className="text-brand-warm-gray text-xs text-center mt-4">
                    No spam. No obligation. Response within 1 hour.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
